import { Box, Button, Grid, TextField, Autocomplete } from "@mui/material";
import TModal from "../../../common/components/TModal";
import { useStores } from "../../../models/helpers/useStores";
import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DepartmentDetail } from "../../../services/phpServices";

interface DepartmentModalProps {
  open: boolean;
  onClose: () => void;
  handleGroupSelect: (groupIds: number[]) => void;
}

interface GroupOption {
  group_id: number;
  group_name: string;
  department_name: string;
}

export default function DepartmentModal({
  open,
  onClose,
  handleGroupSelect,
}: DepartmentModalProps): JSX.Element {
  const {
    appStore: { departments, userProfile, getACLGroup },
  } = useStores();
  const hasInitializedRef = useRef(false);

  const allGroupOptions: GroupOption[] = useMemo(() => {
    return (
      departments?.flatMap((dept: any) =>
        (dept.groups || []).map((group: any) => ({
          group_id: group.group_id,
          group_name: group.group_name,
          department_name: dept.department_name,
        }))
      ) || []
    );
  }, [departments]);

  const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([]);
  const { t } = useTranslation() as any;
  const getAllGroupIds = (data: DepartmentDetail[] = []): number[] => {
    return data.flatMap((d) => d.groups?.map((g: any) => g.group_id) || []);
  };

  const groups = getAllGroupIds(departments);

  const filterGroupIdsWithTimesheetApprove = (groupIds: number[]): number[] => {
    if (userProfile?.IsAdmin === 1) {
      return groupIds;
    }
    const aclGroups = getACLGroup() || {};
    return groupIds.filter(
      (groupId) => aclGroups?.[groupId]?.timesheet?.Approve === true
    );
  };
  const allowedGroups = filterGroupIdsWithTimesheetApprove(groups);

  const handleClose = () => {
    setSelectedGroups([]);
    hasInitializedRef.current = false;
    onClose();
  };

  const handleSubmit = () => {
    handleGroupSelect(selectedGroups.map((g) => g.group_id));
    onClose();
  };

  useEffect(() => {
    if (open && !hasInitializedRef.current && allowedGroups.length > 0) {
      const defaultSelected = allGroupOptions.filter((g) =>
        allowedGroups.includes(g.group_id)
      );
      setSelectedGroups(defaultSelected);
      hasInitializedRef.current = true;
    }
  }, [open, allowedGroups, allGroupOptions]);

  return (
    <TModal
      open={open}
      onClose={onClose}
      title={t("20_00_select_group")}
      size="small"
    >
      <Box sx={{ p: 4 }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={1}
          options={allGroupOptions.filter((g) =>
            allowedGroups.includes(g.group_id)
          )}
          groupBy={(option) => option.department_name}
          getOptionLabel={(option) => option.group_name}
          value={selectedGroups}
          onChange={(_, newValue) => setSelectedGroups(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("20_93_search_role")}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  minHeight: 50,
                  fontSize: "13px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "12px",
                  fontWeight: 400,
                },
              }}
            />
          )}
          isOptionEqualToValue={(option, value) =>
            option.group_id === value.group_id
          }
          sx={{
            "& .MuiAutocomplete-tag": {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiAutocomplete-inputRoot": {
              paddingTop: "2px",
              paddingBottom: "2px",
            },
          }}
        />

        <Grid
          container
          alignItems="center"
          spacing={2}
          sx={{ py: 2, justifyContent: "flex-end", marginTop: "16px" }}
        >
          <Grid item>
            <Button onClick={handleClose} variant="outlined">
              {t("20_00_cancel")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={selectedGroups.length === 0}
            >
              {t("20_00_save")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </TModal>
  );
}
